import type { Table, Reservation } from '../types';

/**
 * Table with computed display status and reservation context
 */
export interface TableWithStatus extends Omit<Table, 'status'> {
  displayStatus: 'available' | 'occupied' | 'reserved';
  currentReservation?: Reservation;
  nextReservation?: Reservation;
}

/**
 * Calculate the display status of a table at a given time
 * Reused by both TableFloorPlan and FloorPlan pages
 */
export function calculateTableStatus(
  table: Table,
  reservations: Reservation[],
  _selectedShift: string,
  currentTime: Date
): TableWithStatus {
  // Business logic constants
  const RESERVATION_DURATION_MILLIS = (2 * 60 + 5) * 60 * 1000; // 2h + 5m
  const PRE_RESERVATION_BUFFER_MILLIS = 2 * 60 * 60 * 1000; // 2 hours

  // All active reservations for this table (across shifts) so carry-over occupancy
  // from a previous shift is still reflected in the current shift view.
  const activeTableReservations = reservations.filter(r =>
    r.table_id === table.id &&
    (r.status === 'confirmed' || r.status === 'in-progress')
  );

  const currentReservation = activeTableReservations.find(r => {
    const reservationTime = new Date(`${r.date}T${r.time}:00`);
    const endTime = new Date(reservationTime.getTime() + RESERVATION_DURATION_MILLIS);
    return currentTime >= reservationTime && currentTime < endTime;
  });

  // Any in-progress reservation for this table today should immediately mark it
  // as occupied, even if the scheduled time is slightly in the future.
  const seatedReservation = activeTableReservations
    .filter(r => r.status === 'in-progress')
    .sort((a, b) =>
      new Date(`${a.date}T${a.time}:00`).getTime() -
      new Date(`${b.date}T${b.time}:00`).getTime()
    )[0];

  // Next reservation is also cross-shift so a morning reservation blocking table 4 at
  // 17:00 will still show the table as reserved when viewing the night-shift at 16:50.
  const nextReservation = activeTableReservations
    .filter(r => {
      const reservationTime = new Date(`${r.date}T${r.time}:00`);
      return reservationTime > currentTime;
    })
    .sort((a, b) => new Date(`${a.date}T${a.time}:00`).getTime() - new Date(`${b.date}T${b.time}:00`).getTime())[0];

  let displayStatus: TableWithStatus['displayStatus'] = 'available';

  // 1. Manual status overrides have the highest priority.
  if (['occupied', 'reserved'].includes(table.status)) {
    if (table.status === 'occupied' || table.status === 'reserved')
      displayStatus = table.status;
  }
  // 2. If there is any in-progress reservation for this table, treat it as occupied
  // immediately (guests are seated), regardless of the scheduled start time.
  else if (seatedReservation) {
    displayStatus = 'occupied';
  }
  // 3. If a reservation is currently active in its time window:
  else if (currentReservation) {
    // Only mark occupied when the active reservation is in-progress (checked-in).
    displayStatus =
      currentReservation.status === 'in-progress'
        ? 'occupied'
        : 'reserved';
  }
  // 4. If there's a future reservation, check if it's within the buffer time.
  else if (nextReservation) {
    const reservationTime = new Date(`${nextReservation.date}T${nextReservation.time}:00`);
    const timeUntilReservation = reservationTime.getTime() - currentTime.getTime();

    if (timeUntilReservation <= PRE_RESERVATION_BUFFER_MILLIS) {
      // The table is needed soon for the next reservation.
      displayStatus = 'reserved';
    } else {
      // The next reservation is far enough in the future to allow for walk-ins.
      displayStatus = 'available';
    }
  }
  // 4. If no other conditions are met, the table is available.
  else {
    displayStatus = 'available';
  }

  return {
    ...table,
    displayStatus,
    // Prefer the seated reservation as the current context when present,
    // otherwise fall back to the time-window-based currentReservation.
    currentReservation: seatedReservation ?? currentReservation,
    nextReservation
  };
}

/**
 * Get hex color for a table status (for Konva canvas)
 */
export function getTableStatusHex(status: 'available' | 'occupied' | 'reserved'): string {
  switch (status) {
    case 'available':
      return '#10b981'; // emerald-500
    case 'occupied':
      return '#ef4444'; // red-500
    case 'reserved':
      return '#3b82f6'; // blue-500
    default:
      return '#64748b'; // slate-500
  }
}

/**
 * Get size dimensions for a table based on seat count
 * Returns width and height in virtual canvas units (1000x800 canvas)
 */
export function getTableSizeForSeats(seats: number): { width: number; height: number } {
  if (seats <= 3) {
    return { width: 50, height: 50 };
  } else if (seats <= 6) {
    return { width: 65, height: 65 };
  } else {
    return { width: 80, height: 80 };
  }
}

/**
 * Compute default layout for tables grouped by zone
 * Arranges tables in a grid (4 columns per zone), zones stacked vertically
 * Returns percentages (0-1) normalized to virtual canvas (1000x800)
 */
export function computeDefaultLayout(tables: Table[]): Map<string, { x_pct: number; y_pct: number }> {
  const layout = new Map<string, { x_pct: number; y_pct: number }>();
  
  // Group tables by zone
  const tablesByZone = new Map<string, Table[]>();
  for (const table of tables) {
    if (!tablesByZone.has(table.zone)) {
      tablesByZone.set(table.zone, []);
    }
    tablesByZone.get(table.zone)!.push(table);
  }

  // Sort zones alphabetically for consistent layout
  const zones = Array.from(tablesByZone.keys()).sort();

  let currentY = 0.1; // Start 10% from top
  const ZONE_HEIGHT = 0.25; // Each zone gets 25% of vertical space
  const COLUMNS = 4;
  const CELL_WIDTH = 1 / COLUMNS;
  const CELL_HEIGHT = ZONE_HEIGHT;

  for (const zone of zones) {
    const zoneTables = tablesByZone.get(zone) || [];
    
    // Sort tables within zone by number
    zoneTables.sort((a, b) => a.number - b.number);

    for (let i = 0; i < zoneTables.length; i++) {
      const table = zoneTables[i];
      const col = i % COLUMNS;
      const row = Math.floor(i / COLUMNS);

      const x_pct = (col + 0.5) * CELL_WIDTH; // Center in cell
      const y_pct = currentY + (row + 0.5) * CELL_HEIGHT; // Center in cell

      layout.set(table.id, {
        x_pct: Math.max(0.05, Math.min(0.95, x_pct)),
        y_pct: Math.max(0.05, Math.min(0.95, y_pct))
      });
    }

    currentY += ZONE_HEIGHT;
  }

  return layout;
}
